import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BUNKER_MIN_PLAYERS, DEFAULT_BUNKER_SETTINGS, DEFAULT_PLAYER_NAMES, type BunkerGameSettings, type TeamSummary } from '@shum/shared';
import { api } from '../lib/api';
import { GameLayout } from './GameLayout';

export function CreateBunkerRoom() {
  const navigate = useNavigate();
  const [names, setNames] = useState<string[]>([...DEFAULT_PLAYER_NAMES]);
  const [hostIndex, setHostIndex] = useState(0);
  const [draft, setDraft] = useState('');
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [teamName, setTeamName] = useState('');
  const [settings, setSettings] = useState<BunkerGameSettings>({ ...DEFAULT_BUNKER_SETTINGS });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    api.listTeams().then(setTeams).catch(() => {});
  }, []);

  const roster = useMemo(() => names.map((name) => name.trim()).filter(Boolean), [names]);
  const hostName = names[hostIndex]?.trim() ?? '';
  const rosterError = validateRoster(roster, settings.maxPlayers);

  const choosePreset = (mode: 'quick' | 'standard') => {
    setSettings(mode === 'quick'
      ? { ...DEFAULT_BUNKER_SETTINGS, mode: 'quick' }
      : { ...DEFAULT_BUNKER_SETTINGS, mode: 'standard', characterReviewSeconds: 90, speechSeconds: 75, discussionSeconds: 180, actionWindowSeconds: 30, votingSeconds: 60 });
  };

  function addPlayer() {
    const name = draft.trim();
    if (!name) return;
    if (roster.some((item) => item.toLocaleLowerCase('ru-RU') === name.toLocaleLowerCase('ru-RU'))) {
      setError('Такое имя уже есть в составе.');
      return;
    }
    if (names.length >= settings.maxPlayers) {
      setError(`В этом матче максимум ${settings.maxPlayers} игроков.`);
      return;
    }
    setNames((current) => [...current, name]);
    setDraft('');
    setError(null);
  }

  function renameAt(index: number, value: string) {
    setNames((current) => current.map((name, itemIndex) => itemIndex === index ? value : name));
  }

  function removeAt(index: number) {
    setNames((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setHostIndex((current) => {
      if (index < current) return current - 1;
      if (index === current) return Math.max(0, Math.min(current, names.length - 2));
      return current;
    });
  }

  function loadTeam(teamId: string) {
    if (!teamId) return;
    api.getTeam(teamId)
      .then((team) => {
        setNames(team.players.map((player) => player.name));
        setHostIndex(0);
        setTeamName(team.name);
        setMessage(`Загружена команда «${team.name}».`);
        setError(null);
      })
      .catch(() => setError('Не удалось загрузить команду.'));
  }

  async function saveTeam() {
    if (!teamName.trim()) {
      setError('Введите название команды.');
      return;
    }
    if (rosterError) {
      setError(rosterError);
      return;
    }
    try {
      await api.createTeam(teamName.trim(), roster);
      setTeams(await api.listTeams());
      setMessage('Команда сохранена.');
      setError(null);
    } catch (reason) {
      setError((reason as Error).message);
    }
  }

  async function createRoom() {
    if (rosterError) {
      setError(rosterError);
      return;
    }
    if (!hostName) {
      setError('Отметьте игрока, который будет ведущим.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const room = await api.createBunkerRoom({ players: roster, creatorName: hostName, settings });
      navigate(`/b/${room.code}`);
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <GameLayout>
      <CreateStyles />
      <span className="bk-create-label">/ Бункер · создать комнату</span>
      <h1 className="bk-create-title">Кто сегодня идёт<br />в <span>бункер</span>?</h1>
      <p className="bk-create-lede">Соберите состав и отметьте ведущего. После создания каждый игрок просто выберет себя по QR-ссылке.</p>
      <div className="bk-create-layout">
        <main>
          <section className="bk-create-panel">
            <div className="bk-create-panel-head"><h2>Состав</h2><span>{roster.length} игроков</span></div>
            <p className="bk-roster-note">Отметьте одного ведущего. Он сможет запускать матч, ставить его на паузу и создавать QR для возвращения игрока.</p>
            <div className="bk-team-row"><input className="bk-create-input" placeholder="Название команды (необязательно)" value={teamName} onChange={(event) => setTeamName(event.target.value)} /></div>
            {teams.length > 0 && <div className="bk-team-row"><select className="bk-create-input" value="" onChange={(event) => loadTeam(event.target.value)}><option value="">Загрузить сохранённую команду…</option>{teams.map((team) => <option key={team.teamId} value={team.teamId}>{team.name} · {team.playerCount} игроков</option>)}</select></div>}
            <div className="bk-roster-list">
              {names.map((name, index) => (
                <div className="bk-roster-row" key={`${index}-${name}`}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <input className="bk-create-input" value={name} placeholder={`Игрок ${index + 1}`} onChange={(event) => renameAt(index, event.target.value)} />
                  <label className="bk-host-check" title="Назначить ведущим">
                    <input type="checkbox" checked={hostIndex === index} onChange={() => setHostIndex(index)} />
                    <span>Ведущий</span>
                  </label>
                  <button className="bk-roster-remove" type="button" aria-label={`Удалить игрока ${index + 1}`} onClick={() => removeAt(index)} disabled={names.length <= 1}>×</button>
                </div>
              ))}
            </div>
            <form className="bk-add-row" onSubmit={(event) => { event.preventDefault(); addPlayer(); }}>
              <input className="bk-create-input" value={draft} placeholder="Имя нового игрока" onChange={(event) => setDraft(event.target.value)} />
              <button className="btn-ghost" type="submit" disabled={roster.length >= settings.maxPlayers}>Добавить</button>
            </form>
            <div className="bk-roster-actions"><button className="btn-ghost" type="button" onClick={saveTeam} disabled={!roster.length}>Сохранить команду</button></div>
            {rosterError && <p className="bk-create-error">{rosterError}</p>}
            {!hostName && <p className="bk-create-error">Выберите ведущего из заполненных имён.</p>}
            {message && <p className="bk-create-ok">{message}</p>}
          </section>

          <section className="bk-create-panel">
            <h2>Режим</h2>
            <div className="bk-preset-row">
              <button className={`bk-preset ${settings.mode === 'quick' ? 'selected' : ''}`} onClick={() => choosePreset('quick')}><strong>Быстрая</strong><span>20–35 минут · 1 карта</span></button>
              <button className={`bk-preset ${settings.mode === 'standard' ? 'selected' : ''}`} onClick={() => choosePreset('standard')}><strong>Стандартная</strong><span>45–90 минут · 2 карты</span></button>
            </div>
            <div className="bk-setting-row">
              <div><strong>Места в бункере</strong><span>Автоматически — половина внесённых игроков.</span></div>
              <select value={settings.capacity ?? 'auto'} onChange={(event) => setSettings((current) => ({ ...current, capacity: event.target.value === 'auto' ? null : Number(event.target.value) }))}>
                <option value="auto">Автоматически</option>
                {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((value) => <option key={value} value={value}>{value} места</option>)}
              </select>
            </div>
            <div className="bk-setting-row">
              <div><strong>Максимум игроков</strong><span>Технический максимум — 24; для больших групп игра займёт больше времени.</span></div>
              <select value={settings.maxPlayers} onChange={(event) => setSettings((current) => ({ ...current, maxPlayers: Number(event.target.value) }))}>
                {[6, 8, 10, 12, 16, 20, 24].map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </div>
            <div className="bk-setting-row">
              <div><strong>Возрастной набор</strong><span>Семейный набор исключает потенциально чувствительный контент.</span></div>
              <select value={settings.contentRating} onChange={(event) => setSettings((current) => ({ ...current, contentRating: event.target.value === 'family' ? 'family' : 'standard' }))}><option value="family">Семейный · 10+</option><option value="standard">Стандартный · 14+</option></select>
            </div>
            <div className="bk-toggle-list">
              <Toggle checked={settings.specialCardsEnabled} onChange={(checked) => setSettings((current) => ({ ...current, specialCardsEnabled: checked }))} label="Специальные карты" note="Иммунитет, двойной голос, раскрытие и обмен багажом." />
              <Toggle checked={settings.randomEventsEnabled} onChange={(checked) => setSettings((current) => ({ ...current, randomEventsEnabled: checked }))} label="Случайные события" note="Бункер может раскрыть новую проблему или ресурс между раундами." />
              <Toggle checked={settings.survivalSimulationEnabled} onChange={(checked) => setSettings((current) => ({ ...current, survivalSimulationEnabled: checked }))} label="Финальная симуляция" note="После отбора система оценит шансы выбранной команды." />
            </div>
            <button className="btn-primary lg" disabled={busy || Boolean(rosterError) || !hostName} onClick={createRoom}>{busy ? 'Создаём комнату…' : 'Создать комнату →'}</button>
            {error && <p className="bk-create-error">{error}</p>}
          </section>
        </main>
        <aside className="bk-create-side">
          <div className="bk-create-panel">
            <span className="bk-create-label">Что создастся</span>
            <h2>Комната со списком</h2>
            <ul><li>Код из шести символов и QR-ссылка.</li><li>Игроки выбирают только имена из подготовленного списка.</li><li>Серверный seed и отдельные приватные карточки.</li><li>Таймеры работают независимо от телефона ведущего.</li><li>Восстановление сессии через локальный токен или код.</li></ul>
          </div>
          <div className="bk-create-panel muted"><span className="bk-create-label">Подсказка</span><p>После создания тот, кто выберет отмеченное имя, получит управление матчем. Остальные открывают QR-код и выбирают себя из того же списка.</p></div>
        </aside>
      </div>
    </GameLayout>
  );
}

function validateRoster(names: string[], maxPlayers: number): string | null {
  if (names.length < BUNKER_MIN_PLAYERS) return `Нужно минимум ${BUNKER_MIN_PLAYERS} игрока.`;
  if (names.length > maxPlayers) return `В настройках указан максимум ${maxPlayers} игроков.`;
  if (new Set(names.map((name) => name.toLocaleLowerCase('ru-RU'))).size !== names.length) return 'Имена игроков не должны повторяться.';
  if (names.some((name) => name.length > 24)) return 'Имя игрока не должно быть длиннее 24 символов.';
  return null;
}

function Toggle({ checked, onChange, label, note }: { checked: boolean; onChange: (checked: boolean) => void; label: string; note: string }) {
  return <label className="bk-toggle"><input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} /><span><strong>{label}</strong><small>{note}</small></span></label>;
}

function CreateStyles() {
  return <style>{`
    .bk-create-label { font: 11px 'JetBrains Mono', monospace; letter-spacing: .15em; text-transform: uppercase; color: var(--ink-faint); }
    .bk-create-title { margin: 12px 0 0; font: 600 clamp(52px, 7vw, 82px)/.95 'Oswald', sans-serif; letter-spacing: -.03em; }
    .bk-create-title span { color: #d86f34; font-family: 'Playfair Display', serif; font-style: italic; }
    .bk-create-lede { max-width: 760px; margin: 20px 0 34px; color: var(--ink-dim); font-size: 17px; line-height: 1.55; }
    .bk-create-layout { display: grid; grid-template-columns: 1.35fr .75fr; gap: 22px; align-items: start; }
    .bk-create-panel { padding: 24px; background: var(--bg2); border: 1px solid var(--line); border-radius: 12px; margin-bottom: 14px; }
    .bk-create-panel h2 { margin: 0 0 18px; font: 500 31px 'Oswald', sans-serif; }
    .bk-create-panel-head { display: flex; justify-content: space-between; gap: 16px; align-items: baseline; }
    .bk-create-panel-head > span { color: #d86f34; font: 11px 'JetBrains Mono', monospace; letter-spacing: .08em; text-transform: uppercase; }
    .bk-roster-note { margin: -7px 0 17px; color: var(--ink-dim); font-size: 13px; line-height: 1.45; }
    .bk-team-row { margin-bottom: 9px; }
    .bk-create-input { width: 100%; box-sizing: border-box; padding: 10px 12px; color: var(--ink); background: var(--bg); border: 1px solid var(--line-strong); border-radius: 7px; font: 14px 'Space Grotesk', system-ui, sans-serif; }
    .bk-create-input:focus { outline: none; border-color: #d86f34; }
    .bk-roster-list { display: flex; flex-direction: column; gap: 7px; margin-top: 14px; }
    .bk-roster-row { display: grid; grid-template-columns: 32px 1fr auto 36px; gap: 8px; align-items: center; }
    .bk-roster-row > span { color: var(--ink-faint); font: 10px 'JetBrains Mono', monospace; letter-spacing: .08em; }
    .bk-host-check { display: inline-flex; align-items: center; gap: 6px; color: var(--ink-dim); font-size: 12px; white-space: nowrap; cursor: pointer; }
    .bk-host-check input { width: 16px; height: 16px; margin: 0; accent-color: #d86f34; cursor: pointer; }
    .bk-roster-remove { width: 36px; height: 36px; color: var(--ink-dim); background: transparent; border: 1px solid var(--line-strong); border-radius: 7px; cursor: pointer; font-size: 21px; line-height: 1; }
    .bk-roster-remove:hover:not(:disabled) { color: #d86f34; border-color: #d86f34; }
    .bk-roster-remove:disabled { opacity: .35; cursor: not-allowed; }
    .bk-add-row { display: grid; grid-template-columns: 1fr auto; gap: 8px; margin-top: 12px; }
    .bk-roster-actions { display: flex; justify-content: flex-start; margin-top: 14px; }
    .bk-preset-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
    .bk-preset { padding: 17px; text-align: left; color: var(--ink); background: var(--bg); border: 1px solid var(--line-strong); border-radius: 9px; cursor: pointer; }
    .bk-preset.selected { border-color: #d86f34; background: color-mix(in srgb, #d86f34 10%, var(--bg)); }
    .bk-preset strong, .bk-preset span { display: block; }
    .bk-preset strong { font: 500 26px 'Oswald', sans-serif; }
    .bk-preset span, .bk-setting-row span, .bk-toggle small { margin-top: 4px; color: var(--ink-dim); font-size: 12px; line-height: 1.4; }
    .bk-setting-row { display: grid; grid-template-columns: 1fr 180px; gap: 16px; padding: 16px 0; border-top: 1px solid var(--line); align-items: center; }
    .bk-setting-row strong, .bk-setting-row span { display: block; }
    .bk-setting-row select { width: 100%; padding: 10px; color: var(--ink); background: var(--bg); border: 1px solid var(--line-strong); border-radius: 7px; }
    .bk-toggle-list { border-top: 1px solid var(--line); margin: 8px 0 20px; }
    .bk-toggle { display: flex; gap: 11px; padding: 14px 0; border-bottom: 1px solid var(--line); cursor: pointer; }
    .bk-toggle input { width: 18px; height: 18px; accent-color: #d86f34; flex: 0 0 auto; margin-top: 2px; }
    .bk-toggle strong, .bk-toggle small { display: block; }
    .bk-create-side ul { color: var(--ink-dim); font-size: 14px; line-height: 1.6; padding-left: 18px; }
    .bk-create-side .muted { background: color-mix(in srgb, #d86f34 7%, var(--bg2)); }
    .bk-create-side .muted p { margin: 8px 0 0; color: var(--ink-dim); font-size: 13px; line-height: 1.5; }
    .bk-create-error { color: var(--danger); margin: 13px 0 0; }
    .bk-create-ok { color: #53cb78; margin: 13px 0 0; }
    @media (max-width: 850px) { .bk-create-layout { grid-template-columns: 1fr; } }
    @media (max-width: 520px) { .bk-preset-row, .bk-setting-row, .bk-add-row { grid-template-columns: 1fr; } }
  `}</style>;
}
